import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

const firestore = firebase.firestore();

const servers = {
  iceServers: [
    {
      urls: [
        "stun:stun1.l.google.com:19302",
        "stun:stun2.l.google.com:19302",
        "stun:stun3.l.google.com:19302",
        "stun:stun4.l.google.com:19302",
      ],
    },
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject",
    },
  ],
  iceCandidatePoolSize: 10,
};

// How long the participant waits for the admin's offer before giving up
const OFFER_WAIT_TIMEOUT_MS = 20000;

// Module-level state — reset on each session
let pc = null;
export let localStream = null;
export let remoteStream = null;
export let callId = null;
export let screenStream = null;
export let cameraStream = null;

// Firestore listener unsubscribers so we can tear them down in hangUp()
let unsubscribeCallDoc = null;
let unsubscribeAnswerCandidates = null;
let unsubscribeOfferCandidates = null;

// UI callbacks
let connectionStateCallback = null;
let callEndedCallback = null;

export function onConnectionStateChange(cb) {
  connectionStateCallback = cb;
  if (pc && cb) cb(pc.connectionState);
}

export function onCallEnded(cb) {
  callEndedCallback = cb;
}

function stopStream(stream) {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
}

// Stop any media left over from a previous (possibly failed) attempt so
// retries don't leak camera/screen capture.
function resetLocalMedia() {
  stopStream(localStream);
  stopStream(screenStream);
  if (cameraStream && cameraStream !== localStream) {
    stopStream(cameraStream);
  }
  localStream = null;
  screenStream = null;
  cameraStream = null;
}

function detachListeners() {
  if (unsubscribeCallDoc) {
    unsubscribeCallDoc();
    unsubscribeCallDoc = null;
  }
  if (unsubscribeAnswerCandidates) {
    unsubscribeAnswerCandidates();
    unsubscribeAnswerCandidates = null;
  }
  if (unsubscribeOfferCandidates) {
    unsubscribeOfferCandidates();
    unsubscribeOfferCandidates = null;
  }
}

function createPeerConnection() {
  if (pc) {
    pc.onconnectionstatechange = null;
    pc.ontrack = null;
    pc.onicecandidate = null;
    pc.close();
  }
  pc = new RTCPeerConnection(servers);
  pc.onconnectionstatechange = () => {
    if (pc && connectionStateCallback) {
      connectionStateCallback(pc.connectionState);
    }
  };
  return pc;
}

// Translate getUserMedia/getDisplayMedia failures into messages the UI can show.
export function mediaErrorMessage(error, isScreenShare) {
  const source = isScreenShare ? "screen" : "camera and microphone";
  switch (error?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return isScreenShare
        ? "Screen sharing was cancelled or blocked. Click Start again and pick a screen to share."
        : "Camera/microphone access was denied. Allow access in your browser settings and retry.";
    case "NotFoundError":
    case "OverconstrainedError":
      return `No usable ${source} was found on this device.`;
    case "NotReadableError":
      return `Your ${source} is already in use by another app. Close it and retry.`;
    case "InvalidStateError":
      return "The browser blocked screen sharing because the page wasn't interacted with recently. Click Start to try again.";
    default:
      return error?.message || `Could not access your ${source}.`;
  }
}

export const NewAdminPermissionsStream = async () => {
  resetLocalMedia();
  createPeerConnection();

  try {
    screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });
  } catch (error) {
    console.error("Error capturing screen:", error);
    throw new Error(mediaErrorMessage(error, true));
  }

  try {
    cameraStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
  } catch (error) {
    console.error("Error capturing camera:", error);
    stopStream(screenStream);
    screenStream = null;
    throw new Error(mediaErrorMessage(error, false));
  }

  const combinedStream = new MediaStream();

  // Track order matters: screen video is added before camera video so the
  // participant can rely on m-line order (mid 0 = screen, mid 1 = camera).
  combinedStream.addTrack(screenStream.getVideoTracks()[0]);
  combinedStream.addTrack(cameraStream.getVideoTracks()[0]);

  const systemAudioTrack = screenStream.getAudioTracks()[0];
  if (systemAudioTrack) {
    combinedStream.addTrack(systemAudioTrack);
  }

  const micTrack = cameraStream.getAudioTracks()[0];
  if (micTrack) {
    combinedStream.addTrack(micTrack);
  }

  combinedStream.getTracks().forEach((track) => {
    pc.addTrack(track, combinedStream);
  });

  const adminRemoteStream = new MediaStream();
  pc.ontrack = (event) => {
    adminRemoteStream.addTrack(event.track);
  };

  remoteStream = adminRemoteStream;
  return { screenStream, cameraStream, remoteStream: adminRemoteStream };
};

export const NewParticipantPermissionsStream = async () => {
  resetLocalMedia();
  createPeerConnection();

  try {
    localStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
  } catch (error) {
    console.error("Error capturing participant camera:", error);
    throw new Error(mediaErrorMessage(error, false));
  }

  localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

  const remoteScreenStream = new MediaStream();
  const remoteCameraStream = new MediaStream();

  pc.ontrack = (event) => {
    const track = event.track;
    if (track.kind === "video") {
      // The admin always adds the screen video track first, so it occupies
      // the lower m-line (mid "0"). Use the transceiver mid when available
      // instead of relying on event arrival order, which isn't guaranteed.
      const mid = parseInt(event.transceiver?.mid, 10);
      const isScreen = Number.isNaN(mid)
        ? !remoteScreenStream.getVideoTracks().length
        : mid === 0;
      if (isScreen) {
        remoteScreenStream.addTrack(track);
      } else {
        remoteCameraStream.addTrack(track);
      }
    } else if (track.kind === "audio") {
      // Audio goes to the screen stream only; adding it to both streams
      // plays it twice through two <video> elements.
      remoteScreenStream.addTrack(track);
    }
  };

  cameraStream = localStream;
  return { localStream, remoteScreenStream, remoteCameraStream };
};

export const callButtonFunction = async () => {
  detachListeners();
  const callDoc = firestore.collection("calls").doc();
  const offerCandidates = callDoc.collection("offerCandidates");
  const answerCandidates = callDoc.collection("answerCandidates");

  callId = callDoc.id;

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      offerCandidates.add(event.candidate.toJSON()).catch(console.error);
    }
  };

  const offerDescription = await pc.createOffer();
  await pc.setLocalDescription(offerDescription);

  await callDoc.set({
    offer: { sdp: offerDescription.sdp, type: offerDescription.type },
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

  // Listen for remote answer (and for the doc being deleted = call ended)
  unsubscribeCallDoc = callDoc.onSnapshot((snapshot) => {
    if (!snapshot.exists) {
      if (callEndedCallback) callEndedCallback();
      return;
    }
    const data = snapshot.data();
    if (!pc || pc.signalingState === "closed") return;
    if (!pc.currentRemoteDescription && data?.answer) {
      pc.setRemoteDescription(new RTCSessionDescription(data.answer)).catch(
        console.error,
      );
    }
  });

  // When answered, add answer ICE candidates
  unsubscribeAnswerCandidates = answerCandidates.onSnapshot((snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added") {
        if (!pc || pc.signalingState === "closed") return;
        pc.addIceCandidate(new RTCIceCandidate(change.doc.data())).catch(
          console.error,
        );
      }
    });
  });

  return callId;
};

// Wait (via a snapshot listener) until the call doc contains an offer,
// instead of a single one-shot read that fails if the admin is still setting up.
function waitForOffer(callDoc) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const unsubscribe = callDoc.onSnapshot(
      (snapshot) => {
        const data = snapshot.data();
        if (data?.offer && !settled) {
          settled = true;
          clearTimeout(timer);
          unsubscribe();
          resolve(data.offer);
        }
      },
      (error) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          unsubscribe();
          reject(error);
        }
      },
    );
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        unsubscribe();
        reject(
          new Error(
            "The call is no longer available. Ask your friend to send a new invite.",
          ),
        );
      }
    }, OFFER_WAIT_TIMEOUT_MS);
  });
}

export const answerButtonFunction = async (id) => {
  detachListeners();
  callId = id;

  const callDoc = firestore.collection("calls").doc(callId);
  const answerCandidates = callDoc.collection("answerCandidates");
  const offerCandidates = callDoc.collection("offerCandidates");

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      answerCandidates.add(event.candidate.toJSON()).catch(console.error);
    }
  };

  const offer = await waitForOffer(callDoc);

  await pc.setRemoteDescription(new RTCSessionDescription(offer));

  const answerDescription = await pc.createAnswer();
  await pc.setLocalDescription(answerDescription);

  await callDoc.update({
    answer: { type: answerDescription.type, sdp: answerDescription.sdp },
  });

  // Listen for offer ICE candidates, and detect the admin ending the call
  unsubscribeCallDoc = callDoc.onSnapshot((snapshot) => {
    if (!snapshot.exists && callEndedCallback) {
      callEndedCallback();
    }
  });
  unsubscribeOfferCandidates = offerCandidates.onSnapshot((snapshot) => {
    snapshot.docChanges().forEach((change) => {
      if (change.type === "added") {
        if (!pc || pc.signalingState === "closed") return;
        pc.addIceCandidate(new RTCIceCandidate(change.doc.data())).catch(
          console.error,
        );
      }
    });
  });
};

export const hangUp = async () => {
  // Tear down Firestore listeners first so they don't fire during cleanup
  detachListeners();
  callEndedCallback = null;
  connectionStateCallback = null;

  resetLocalMedia();

  if (pc) {
    pc.onconnectionstatechange = null;
    pc.ontrack = null;
    pc.onicecandidate = null;
    pc.close();
    pc = null;
  }

  if (callId) {
    const endedCallId = callId;
    callId = null;
    try {
      const callDoc = firestore.collection("calls").doc(endedCallId);
      const offerCandidates = callDoc.collection("offerCandidates");
      const answerCandidates = callDoc.collection("answerCandidates");

      const deleteCollection = async (collectionRef) => {
        const snapshot = await collectionRef.get();
        const batch = firestore.batch();
        snapshot.docs.forEach((doc) => batch.delete(doc.ref));
        await batch.commit();
      };

      await deleteCollection(offerCandidates);
      await deleteCollection(answerCandidates);
      await callDoc.delete();
    } catch (error) {
      console.error("Error cleaning up Firestore call data:", error);
    }
  }

  remoteStream = null;
};
